package dev.kuml.portal.web.editor

/** One peer's currently-known presence -- see `EditorScreen.kt`'s `presenceState`, keyed by the
 * SAME hex-encoded connection token [CollabFrame.decodePresence] decodes. */
public data class PeerPresence(
    val tokenHex: String,
    val cursor: Pair<Double, Double>? = null,
    val selection: List<String> = emptyList(),
    val cursorUpdatedAtMs: Double = 0.0,
)

/**
 * R-28: pure bookkeeping for the presence state a diagram editor session tracks about its peers --
 * DOM-free (see `PresenceOverlay.kt`'s `repaint`, the DOM-touching counterpart that reads this map),
 * so the merge/TTL/sort rules are directly unit-testable (see `PresenceStateTest`).
 */
public object PresenceState {
    /** A peer's cursor is hidden once this many milliseconds have passed since its last update
     * without a fresh one -- covers the "closed the laptop lid" case a `pointerleave`/explicit idle
     * payload never fires for (design-team ruling). [PeerPresence.selection], by contrast, has NO
     * such TTL -- a stale but still genuinely selected element keeps showing as selected until the
     * peer's connection itself drops (a presence frame with `present = false`, handled by
     * [withoutPeer]), not silently vanish after 10 seconds of inactivity while the peer is still
     * legitimately connected and looking at that element. */
    public const val CURSOR_TTL_MS: Double = 10_000.0

    /** Merges [decoded] into [state] for [tokenHex] -- a full replace of BOTH
     * [PeerPresence.cursor] and [PeerPresence.selection] (every incoming payload is a complete
     * snapshot, never a delta -- see [PresencePayload]'s class KDoc), never a partial merge with the
     * previous entry. [now] becomes the new [PeerPresence.cursorUpdatedAtMs] -- even when
     * [DecodedPresence.cursor] is `null` (an idle/leave payload), so a subsequent [visibleCursorOf]
     * correctly reports "no cursor" immediately rather than waiting out a stale TTL window. */
    public fun withUpdate(
        state: Map<String, PeerPresence>,
        tokenHex: String,
        decoded: DecodedPresence,
        now: Double,
    ): Map<String, PeerPresence> = state + (tokenHex to PeerPresence(tokenHex, decoded.cursor, decoded.selection, now))

    /** Removes [tokenHex] entirely -- called when its presence frame reports `present = false` (the
     * connection left, or explicitly cleared its presence via [PresencePayload.EMPTY]). */
    public fun withoutPeer(
        state: Map<String, PeerPresence>,
        tokenHex: String,
    ): Map<String, PeerPresence> = state - tokenHex

    /** `null` when [peer] has no cursor at all, OR when [CURSOR_TTL_MS] has elapsed since
     * [PeerPresence.cursorUpdatedAtMs] -- otherwise [PeerPresence.cursor] unchanged. */
    public fun visibleCursorOf(
        peer: PeerPresence,
        now: Double,
    ): Pair<Double, Double>? {
        val cursor = peer.cursor ?: return null
        return if (now - peer.cursorUpdatedAtMs > CURSOR_TTL_MS) null else cursor
    }

    /** Every `tokenHex` in [state] currently selecting [elementId], sorted ascending -- a stable
     * stacking order for `PresenceOverlay.kt`'s ring-offset stack (see that file's KDoc). */
    public fun peersSelecting(
        state: Map<String, PeerPresence>,
        elementId: String,
    ): List<String> =
        state.values
            .filter { elementId in it.selection }
            .map { it.tokenHex }
            .sorted()
}
